/*
 * Studio v2 — the shell (<studio-v2>). Owns the state and runs the loop:
 *   route → render the screen → the screen emits → rules validate + apply → route again.
 * Also: entry presets from the URL, the AI client (mvsport-studio-ai), the offers list, the
 * slot inspector (?slots=1), analytics (studio2_* events), and the cart hand-off, which writes
 * the same cart lines as the live Studio (assets/decorate.js), so the cart page, its minimum
 * check and order handling are unchanged.
 */
(function () {
  const C = window.StudioCore, R = window.StudioRender;
  const SAMPLE_LIMIT = 3;
  const WEAR_LIMIT = 3; // model shots per visit (experiment studio-model-shot): about $0.07 and 10 s each
  const artNotes = (info) => window.StudioPrint.notes(info);

  function el(tag, attrs = {}, kids = []) {
    const e = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null || v === false) continue;
      if (k === 'class') e.className = v;
      else if (k === 'text') e.textContent = v;
      else if (k === 'style') e.style.cssText = v;
      else if (k.startsWith('on')) e.addEventListener(k.slice(2), v);
      else if (k === 'checked' || k === 'selected' || k === 'disabled' || k === 'hidden') { e[k] = !!v; if (v) e.setAttribute(k, ''); }
      else if (k === 'value') e.value = v;
      else e.setAttribute(k, v === true ? '' : v);
    }
    [].concat(kids).forEach((c) => c != null && c !== false && e.append(c));
    return e;
  }
  const track = (name, props) => { try { if (window.mvTrack) window.mvTrack(name, props); } catch (_) {} };
  // PostHog session id, sent with every AI call so the Worker's log joins to the session recording.
  const isInternal = () => { try { return new URLSearchParams(location.search).get('internal') === '1' || localStorage.getItem('mv_internal') === '1'; } catch (_) { return false; } };
  // The PostHog person, sent when a group is made, so the group service's own events (paid, closed) land on that person.
  const phId = () => { try { const ph = window.posthog; const v = ph && typeof ph.get_distinct_id === 'function' ? ph.get_distinct_id() : null; return typeof v === 'string' ? v : null; } catch (_) { return null; } };
  const sid = () => { try { const ph = window.posthog; const v = ph && typeof ph.get_session_id === 'function' ? ph.get_session_id() : null; return typeof v === 'string' ? v : null; } catch (_) { return null; } };
  // Our own browsers (?internal=1, see snippets/posthog.liquid) may point the Studio at the staging AI
  // Worker with ?ai_endpoint=https://mvsport-studio-ai-staging.cf.dpeyserapps.com (before/after runs).
  const aiOverride = (() => {
    try {
      const q = new URLSearchParams(location.search), v = q.get('ai_endpoint') || sessionStorage.getItem('sv_ai_endpoint') || '';
      const internal = q.get('internal') === '1' || localStorage.getItem('mv_internal') === '1';
      if (!internal || !/^https:\/\/mvsport-studio-ai(-[a-z]+)?\.cf\.dpeyserapps\.com$/.test(v)) return null;
      sessionStorage.setItem('sv_ai_endpoint', v);
      return v;
    } catch (_) { return null; }
  })();
  const rid = (p) => `${p}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  // The group link's back end (revamp Phase 3 item 5; launchpad task shopmvsport-revamp-phase3, groups/). Our own browsers
  // (?internal=1) may use the test copy with ?groups=next.
  const GROUPS = (() => {
    try {
      const q = new URLSearchParams(location.search), internal = q.get('internal') === '1' || localStorage.getItem('mv_internal') === '1';
      if (internal && (q.get('groups') || sessionStorage.getItem('sv_groups')) === 'next') { sessionStorage.setItem('sv_groups', 'next'); return 'https://mvsport-groups-next.cf.dpeyserapps.com'; }
    } catch (_) {}
    return 'https://mvsport-groups.cf.dpeyserapps.com';
  })();

  // Image → small JPEG data URL for the AI (≤ 768 px), on white; null when this device cannot read it.
  // A phone does not always decode a photo the first time: on #MV1544's visit (Android Chrome, 2026-10-14) the
  // same 1.2 MB JPEG failed to load 17 times of 20, so the Studio sent no image, the AI Worker answered "image
  // (data URL) required", and the Studio showed "We print it as it is" for a dog photo. So: the blob URL, then
  // the file decoded directly (createImageBitmap), then the file read into a data URL; and once more after a pause.
  async function smallDataUrl(src, max = 768, file = null) {
    const draw = (img, w, h) => {
      if (!w || !h) return null;
      const k = Math.min(1, max / Math.max(w, h));
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
      const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height);
      const url = c.toDataURL('image/jpeg', 0.85);
      return /^data:image\/jpeg;base64,./.test(url) ? url : null; // "data:," = the canvas failed
    };
    const viaUrl = async (u) => { const img = await R.loadImage(u); return img ? draw(img, img.naturalWidth, img.naturalHeight) : null; };
    const viaBitmap = async () => { if (!file || !window.createImageBitmap) return null; const b = await createImageBitmap(file); try { return draw(b, b.width, b.height); } finally { b.close && b.close(); } };
    const viaReader = async () => {
      if (!file) return null;
      const u = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = () => res(null); fr.readAsDataURL(file); });
      return u ? viaUrl(u) : null;
    };
    for (let round = 0; round < 2; round++) {
      for (const way of [() => viaUrl(src), viaBitmap, viaReader]) {
        try { const out = await way(); if (out) return out; } catch (_) {}
      }
      await new Promise((r) => setTimeout(r, 600));
    }
    return null;
  }

  class StudioV2 extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.d = JSON.parse(document.getElementById('StudioData').textContent);
      C.setMoneyFormat(this.d.moneyFormat);
      this.cat = C.makeCatalog(this.d, R.zoneKeys);
      this.cat.conflicts = (window.DecorateZones && window.DecorateZones.CONFLICTS) || {};
      this.cat.defaultHandle = this.d.defaultHandle && C.product(this.cat, this.d.defaultHandle) ? this.d.defaultHandle : (this.cat.products[0] || {}).handle;
      const params = new URLSearchParams(location.search);
      const pre = C.presetsFromUrl(params, this.cat, this.d.pageProduct);
      if (this.d.pageProduct && pre.entry.source === 'direct') pre.entry.source = 'pdp';
      this.ctx = { entry: pre.entry, history: [], cat: this.cat, device: window.matchMedia('(max-width: 749px)').matches ? 'phone' : 'desktop', debug: params.get('slots') === '1' || this.dataset.debug === 'true' };
      this.state = C.createState();
      const r = C.apply(this.state, pre.updates, this.cat);
      this.state = r.state;
      // Hand-off from another page (the homepage box): words and a photo, kept in sessionStorage
      // because a file cannot travel in a URL. ?go=1 starts the ideas at once.
      try {
        const h = JSON.parse(sessionStorage.getItem('sv_handoff') || 'null');
        sessionStorage.removeItem('sv_handoff');
        if (h && Date.now() - h.at < 15 * 60 * 1000) {
          const brief = { text: h.text || '', image: null };
          if (h.image) {
            const bin = atob(h.image.split(',')[1]), u8 = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
            const file = new File([u8], h.name || 'photo.jpg', { type: (h.image.match(/^data:([^;]+)/) || [])[1] || 'image/jpeg' });
            brief.image = { file, name: file.name, src: URL.createObjectURL(file), cls: null };
          }
          this.state.brief = brief;
          pre.entry.source = pre.entry.source === 'direct' ? 'home' : pre.entry.source;
        }
      } catch (_) { /* storage blocked: the shopper adds the photo here instead */ }
      // ?start=group (a group ad, or a link): straight into the group flow's step 1 (group-first fork).
      if (params.get('start') === 'group') { this.ctx.gflow = { from: 'link' }; track('studio2_group_started', this.props({ from: 'link' })); }
      // &picked=1: the link already chose the garment and colour (the carousel's hoodie cards, MC1/MC6, through the team
      // page's redirect; task shopmvsport-carousel-leaks): skip step 1 and open the design doors with it set.
      if (this.ctx.gflow && params.get('picked') === '1' && pre.updates.some((u) => u.slot === 'style')) { this.ctx.gflow.styled = true; track('studio2_group_step', this.props({ step: 1, preset: true })); }
      // Ideas landing pages (Shai's redesign part 2): &pay=members|organiser and &names=1|0 are step 3's starting choices.
      // The big button opens the first screen without start=group (2026-10-27), so they are kept in ctx.groupPreset for a
      // group the shopper starts from inside the Studio (studio-groupflow.js gsetup reads either).
      { const gp = {}; if (/^(members|organiser)$/.test(params.get('pay') || '')) gp.pay = params.get('pay'); if (/^[01]$/.test(params.get('names') || '')) gp.names = params.get('names') === '1'; if (Object.keys(gp).length) { this.ctx.groupPreset = gp; if (this.ctx.gflow) this.ctx.gflow.preset = gp; } }
      // Group visitors (a group ad: src G*, MG*, V1; a group entry on the site) see sizes open at 12 with the 12-piece price,
      // the price the ad and the first screen promised (session review 2026-10-23: 13 left the sizes screen at 1 piece).
      if (/^(G|MG)[A-Za-z0-9-]*$|^V1$|^(home|nav|pdp|rack)-group$|^group-link$|^lp-[a-z]+$/.test(pre.entry.ad || '')) this.state.defaultQty = 12;
      pre.entry.autostart = params.get('go') === '1' && !!(this.state.brief.text || this.state.brief.image || pre.entry.brief);
      if (pre.notes.length || r.rejected.length) console.info('[studio v2] presets', pre.notes, r.rejected);
      this.uploads = new Map();
      this.memo = {}; // screens' scratch space that outlives one visit (e.g. design versions); never order state
      this.$screen = this.querySelector('[data-screen]');
      this.$offers = this.querySelector('[data-offers]');
      this.$sheet = this.querySelector('[data-sheet]');
      this.$box = this.querySelector('[data-sheet-box]');
      this.$slots = this.querySelector('[data-slots]');
      this.querySelector('[data-sheet-close]').addEventListener('click', () => this.emit({ type: 'handoff', to: 'core' }));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.state.side) this.emit({ type: 'handoff', to: 'core' }); });
      this.classList.toggle('sv--debug', this.ctx.debug);
      // Shipping estimate + "arrives by" for the work order (H1/H4, Cal 2026-10-12), and what is already in the
      // cart (so the $50 free-shipping line counts the whole cart).
      this.ship = null; this.cartCents = 0; this.added = null;
      this.loadShip(); this.loadCart();
      track('studio2_opened', this.props({ presets: pre.updates.map((u) => u.slot) }));
      // A group link (?group=CODE, plus &key= for the organiser): the member or organiser page instead of the Studio.
      if (params.get('group') && window.StudioGroup) { window.StudioGroup.page(this, params.get('group'), params.get('key')); return; }
      // The design from before a reload or a back step in this tab (bug 3, sweep 2026-10-21): restore it, then draw.
      // Typing in any field saves too, so the group form and the words survive.
      ['input', 'change'].forEach((t) => this.addEventListener(t, () => this.keep(), true));
      // The link in a quote email (?quote=<id>, "Email me this quote" on the sizes screen): the saved design and sizes.
      if (/^[A-Za-z0-9_-]{16}$/.test(params.get('quote') || '')) {
        this.$screen.replaceChildren(el('p', { class: 'sv-fine sv-keep__wait', text: 'Opening your quote…' }));
        this.openQuote(params.get('quote')).finally(() => this.render());
        return;
      }
      if (this.keepable(params, pre)) {
        this.$screen.replaceChildren(el('p', { class: 'sv-fine sv-keep__wait', text: 'Opening your design…' }));
        window.StudioKeep.load().then((k) => { if (k) this.restore(k); }).catch(() => {}).finally(() => { this.render(); if (this.restored) this.keptNote(); });
        return;
      }
      this.render();
    }
    // A kept design is restored unless this visit asks for something else: a group link, a door or words from the home
    // page, a different garment, or a group start when the kept design was not a group.
    keepable(params, pre) {
      const K = window.StudioKeep, m = K && K.peek();
      if (!m) return false;
      if (params.get('door') || params.get('brief') || params.get('go') === '1' || this.state.brief.text || this.state.brief.image) return false;
      const style = (pre.updates.find((u) => u.slot === 'style') || {}).value;
      if (style && m.style && style !== m.style) return false;
      if (params.get('start') === 'group' && !m.gflow) return false;
      return true;
    }
    restore(k, from = 'keep') {
      const s = k.state || {};
      if (!s.slots) return;
      const brief = s.brief || this.state.brief;
      if (k.briefDraft && !brief.text) brief.text = k.briefDraft;
      this.state = { ...this.state, slots: { ...this.state.slots, ...s.slots }, brief, proposals: s.proposals || null, read: s.read || '', side: null };
      if (k.gflow) this.ctx.gflow = k.gflow;
      Object.assign(this.memo, k.memo || {});
      this.pickedTitle = k.pickedTitle || null;
      if (k.uploadClass) this.uploadClass = k.uploadClass;
      this.restored = true;
      if (from === 'keep') track('studio2_restored', this.props({ age_s: Math.round((Date.now() - (k.at || Date.now())) / 1000), proposals: (s.proposals || []).length, gflow: !!k.gflow }));
      if (this.state.proposals) this.drawArt(); // ideas whose drawing the reload cut off
    }
    keptNote() {
      const t = this.querySelector('[data-toast]');
      t.replaceChildren(el('span', { text: 'We kept your design from before. ' }), el('button', { type: 'button', class: 'sv-link', text: 'Start over', onclick: () => { track('studio2_restore_reset', this.props()); window.StudioKeep.clear().then(() => location.reload()); } }));
      t.hidden = false;
      clearTimeout(this._toast);
      this._toast = setTimeout(() => (t.hidden = true), 9000);
    }
    // Save the design for this tab (studio-keep.js). Not after it went in the cart: the next visit starts clean.
    keep() {
      const K = window.StudioKeep;
      if (!K || !this.cat || this.added || this.kept === false) return;
      K.save({ style: C.effective(this.state, this.cat).style.value, gflow: !!this.ctx.gflow, data: this.snapshot() });
    }
    // What keep() saves and a quote stores: the slots, the brief, the ideas and the screens' memo.
    snapshot(memoKeys = ['door', 'groupFirst', 'doorPicked', 'autostarted', 'place', 'clean', 'workshop', 'gsetup', 'proof', 'fundraiser', 'seen', 'wearView']) {
      const memo = {};
      memoKeys.forEach((x) => { if (this.memo[x] !== undefined && this.memo[x] !== 'busy') memo[x] = this.memo[x]; });
      const ta = this.$screen && this.$screen.dataset.screen === 'brief' ? this.$screen.querySelector('textarea.sv-brief__text') : null;
      const { slots, brief, proposals, read } = this.state;
      return { at: Date.now(), state: { slots, brief, proposals, read }, gflow: this.ctx.gflow || null, memo, pickedTitle: this.pickedTitle || null, uploadClass: this.uploadClass || null, briefDraft: ta ? ta.value : '' };
    }

    // ------------------------------------------------------------------ "Email me this quote" (session review 2026-10-23, fix B)
    // 5 visitors priced 5 to 35 pieces ($74 to $636) on the sizes screen and left with no way to keep the quote. This saves the
    // design (its files uploaded like a checkout's) with the garment, colour and sizes fixed, and the group service
    // (mvsport-groups /api/quotes) keeps it 90 days and sends one email: the price, the mockup and a link that brings it back.
    async emailQuote(email) {
      const gates = C.gates(this.state, this.cat);
      if (gates.length) throw Object.assign(new Error(gates.map((g) => g.message).join(' ')), { user: true });
      const e = C.effective(this.state, this.cat), q = C.quote(e, this.cat), p = q.product, color = e.colorway.value, decs = e.decorations.value || [];
      const t0 = Date.now(), req = `${rid('Q')}-${Date.now().toString(36)}`;
      track('studio2_quote_email_requested', this.props({ pieces: q.pieces, total: q.total / 100, unit: q.unit / 100 }));
      try {
        await this.designsDraw(p, color, decs);
        await this.screenDesigns(decs);
        const g = await R.garment(p, color);
        const mockCanvas = await R.mockup(p, color, decs);
        if (mockCanvas.missing) throw Object.assign(new Error('Your design did not load. Go back and add the file again, as a PNG or JPG.'), { user: true });
        // Garment, colour and sizes are fixed in the saved slots, so the link opens the same order on any page and device.
        const snap = this.snapshot(['door', 'place', 'clean', 'proof', 'fundraiser', 'seen']);
        const fix = (k, value) => ({ ...snap.state.slots[k], value, status: 'confirmed', source: (snap.state.slots[k] && snap.state.slots[k].source) || 'shopper' });
        snap.state = { ...snap.state, proposals: null, slots: { ...snap.state.slots, style: fix('style', e.style.value), colorway: fix('colorway', color), sizes: fix('sizes', e.sizes.value) } };
        const packed = window.StudioKeep.pack(snap);
        // AI art arrives as a data: URL; in a quote it travels as a file like the uploads (the saved design stays a few KB).
        const inline = (v) => {
          if (typeof v === 'string' && /^data:[^,;]{1,80};base64,/.test(v) && v.length > 1000) {
            const [head, b64] = v.split(','), bin = atob(b64), u8 = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
            packed.files.push(new Blob([u8], { type: (head.match(/^data:([^;,]+)/) || [])[1] || 'image/png' }));
            return { __blob: packed.files.length - 1 };
          }
          if (Array.isArray(v)) return v.map(inline);
          if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, inline(x)]));
          return v;
        };
        packed.data = inline(packed.data);
        const [mockup, ...urls] = await Promise.all([
          this.store(await R.toBlob(mockCanvas, 'image/jpeg', 0.85), 'quote-mockup.jpg'),
          ...packed.files.map((b, i) => this.store(b, b.name || `quote-file-${i + 1}.${(b.type.split('/')[1] || 'bin').replace('jpeg', 'jpg')}`)),
        ]);
        const how = (i) => (q.type && q.type.ink ? `${q.type.services.indexOf(q.fees[i].sv) + 1} color screen print` : String((q.fees[i] && q.fees[i].sv.label) || 'full color').toLowerCase());
        const sz = e.sizes.value || {};
        const body = {
          req, email, mockup, internal: isInternal(), ph: phId(),
          design: { data: packed.data, files: urls.map((url, i) => ({ url, name: packed.files[i].name || '', type: packed.files[i].type || '' })) },
          summary: { product: p.title, color, prints: decs.map((d, i) => `${this.describe(p, color, d, g).label} · ${how(i)}`), sizes: p.sizes.filter((x) => sz[x] > 0).map((x) => [x, sz[x]]), pieces: q.pieces, each: q.unit, total: q.total },
        };
        let r, j;
        for (let i = 0; ; i++) { // the same request id on a retry: the service sends one email for it, ever
          try { r = await fetch(this.groupsUrl('/api/quotes'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }); j = await r.json().catch(() => ({})); break; } catch (err) { if (i >= 1) throw err; await new Promise((res) => setTimeout(res, 800)); }
        }
        if (!r.ok || !j.id) throw Object.assign(new Error(j.error || `The email did not send (${r.status}). Try again.`), { user: !!j.error });
        if (window.mvIdentify) window.mvIdentify(email, { email });
        return j;
      } catch (err) {
        track('studio2_quote_email_failed', this.props({ error: String(err.message || err).slice(0, 300), ms: Date.now() - t0 }));
        throw err;
      }
    }
    // ?quote=<id>: the design and sizes from the quote email, then the sizes screen. On any failure the Studio opens as usual.
    async openQuote(id) {
      try {
        const r = await fetch(this.groupsUrl(`/api/quotes/${id}`));
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.design) throw Object.assign(new Error(j.error || 'This quote link did not open.'), { user: !!j.error });
        const files = await Promise.all(j.design.files.map(async (f) => {
          const b = await (await fetch(f.url)).blob();
          return new File([b], f.name || 'file', { type: f.type || b.type });
        }));
        this.restore(window.StudioKeep.unpack(j.design.data, files), 'quote');
        const s = j.summary || {};
        track('studio2_quote_restored', this.props({ quote: id, pieces: s.pieces, total: s.total / 100, age_d: Math.floor((Date.now() - Date.parse(j.created_at)) / 86400000) }));
        try { const u = new URL(location.href); u.searchParams.delete('quote'); history.replaceState(history.state, '', u); } catch (_) {} // a reload keeps what is changed from here
        this.toast('Your quote is back: the same design and sizes.');
      } catch (err) {
        track('studio2_quote_restore_failed', this.props({ quote: id, error: String(err.message || err).slice(0, 200) }));
        this.toast(err.user ? err.message : 'This quote link did not open. Try it again, or start a new design.');
      }
    }
    props(extra) {
      const e = C.effective(this.state, this.cat);
      return Object.assign({ studio: 'v2', entry: this.ctx.entry.source, ad: this.ctx.entry.ad, style: e.style.value, color: e.colorway.value, method: e.method.value, filled: C.SLOTS.filter((k) => C.filled(this.state.slots[k])) }, extra || {});
    }

    // ------------------------------------------------------------------ the loop
    render() {
      if (this.ctx.gflow) this.state.defaultQty = 12; // a group order in progress counts as a group visitor too
      const r = C.route(this.state, this.cat, this.ctx);
      this.route = r;
      const core = r.core;
      if (core && core.id !== this.shownCore) {
        this.ctx.history.push(core.id);
        this.shownCore = core.id;
        track('studio2_screen_viewed', this.props({ screen: core.id, kind: 'core', seq: this.ctx.history.length }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      this.paint(this.$screen, core, r.snap);
      this.dataset.screen = core ? core.id : '';
      // The logo-as-is path (Phase 2) has "Place your logo" as step 2 where the AI path has "Pick an idea".
      // The lettering screen (Phase 3) is step 2 of the words path, "Your lettering".
      const order = ['brief', 'proposals', 'review'], at = order.indexOf(core && (core.id === 'place' || core.id === 'letter') ? 'proposals' : core ? core.id : '');
      const decsNow = C.effective(this.state, this.cat).decorations.value || [];
      const asIs = decsNow.some((d) => d.design && d.design.asIs) && !this.state.proposals;
      const letters = decsNow.some((d) => d.design && d.design.lettering && !d.design.backText) && !this.state.proposals;
      // The group flow (group-first fork) has its own three steps. Labels from Shai's redesign (2026-10-23, SPEC 6.5, labels
      // only): "Your design" (garment and design screens), "Get your link" (gsetup; "Set up your group" until 2026-11-01), "Share the link" (the share page).
      const gsteps = this.querySelector('[data-gsteps]'), route = this.querySelector('[data-route]');
      if (gsteps) {
        const gs = this.ctx.gflow ? (core && core.id === 'gsetup' ? 2 : 1) : 0;
        // The group-first first screen (no door picked yet) shows neither bar (studio-screens.js brief paintPaper toggles it).
        gsteps.hidden = !gs; if (route) route.hidden = !!gs || (!!core && core.id === 'brief' && !!this.memo.groupFirst && !this.memo.door);
        gsteps.querySelectorAll('li').forEach((li, i) => { li.classList.toggle('is-on', i + 1 === gs); li.classList.toggle('is-done', i + 1 < gs); });
      }
      this.querySelectorAll('[data-route] li').forEach((li) => {
        const i = order.indexOf(li.dataset.op); li.classList.toggle('is-on', i === at); li.classList.toggle('is-done', i < at);
        // On the first screen, the door picked says which step 2 comes (bug 6: the logo door showed "Pick an idea").
        const door = core && core.id === 'brief' ? this.memo.door : null;
        if (li.dataset.op === 'proposals') li.lastChild.textContent = asIs || door === 'logo' ? ' Place your logo' : letters || door === 'words' ? ' Your lettering' : ' Pick an idea';
      });
      const side = this.state.side ? C.screen(this.state.side) : null;
      if (side) {
        if (this.shownSide !== side.id) { this.ctx.history.push(side.id); track('studio2_screen_viewed', this.props({ screen: side.id, kind: 'side' })); }
        this.shownSide = side.id;
        this.$sheet.hidden = false;
        document.body.classList.add('sv-locked');
        this.paint(this.$box, side, r.snap);
      } else {
        this.shownSide = null;
        this.$sheet.hidden = true;
        document.body.classList.remove('sv-locked');
      }
      this.renderOffers(r);
      this.renderSlots(r.snap);
      this.paintArrives();
      this.keep();
    }
    // The arrival date in the footer and in the first screen's trust strip. They fill in when /api/ship answers, without
    // re-rendering the screen (which would drop what the shopper has typed).
    paintArrives() {
      const day = this.ship && this.ship.arrivesBy ? C.shipDay(this.ship.arrivesBy) : null;
      if (!day) return;
      this.querySelectorAll('[data-arrives-inline]').forEach((n) => { n.textContent = `, arrives by ${day}`; });
      this.querySelectorAll('[data-arrives]').forEach((n) => { n.textContent = `Free delivery on every order · ships in 2 business days, arrives by ${day}${this.ship.known ? '' : ' in most of the US'}`; });
    }
    api(snap) {
      return {
        snap, ctx: this.ctx, cat: this.cat, C, R, el, examples: this.d.examples || {}, money: C.money, email: this.d.email || null, reviews: this.d.reviews || null, brief: this.state.brief, pickedTitle: this.pickedTitle, busy: this.busy, busyDest: this.busyDest, ship: this.ship, cartCents: this.cartCents, cartGrams: this.cartGrams, added: this.added, presetQty: this.state.defaultQty || 1,
        emit: (e) => this.emit(e), track: (n, p) => track(n, this.props(p)),
        ai: { propose: (b) => this.propose(b), classify: (im) => this.classify(im), edit: (b) => this.editArt(b) }, memo: this.memo,
        group: { create: (o) => this.createGroup(o) },
        quote: { send: (email) => this.emailQuote(email) },
        mock: (box, p, color, decs, opts) => this.mock(box, p, color, decs, opts),
        wearView: (stage, mockBox, p, color, decs) => this.wearView(stage, mockBox, p, color, decs),
      };
    }
    paint(host, screen, snap) {
      host.replaceChildren();
      host.className = host === this.$screen ? 'sv-screen' : 'sv-sheet__box'; // drop classes a previous screen set
      host.dataset.screen = screen ? screen.id : '';
      if (!screen) { host.append(el('p', { class: 'sv-error', text: 'Nothing to show. Reload the page.' })); return; }
      try { screen.render(host, this.api(snap)); } catch (err) {
        console.error(`[studio v2] screen ${screen.id}`, err);
        host.append(el('p', { class: 'sv-error', text: `This step failed to load (${err.message}).` }));
      }
    }
    renderOffers(r) {
      const show = !this.state.side && r.core && r.core.id === 'review' && r.offers.length;
      this.$offers.hidden = !show;
      if (!show) return;
      this.$offers.replaceChildren(
        el('h2', { class: 'sv-mono', text: 'Optional' }),
        ...r.offers.map((s) => el('button', { type: 'button', class: 'sv-offer', 'data-offer': s.id, onclick: () => { track('studio2_offer_opened', this.props({ screen: s.id })); this.emit({ type: 'handoff', to: 'screen', screen: s.id }); } }, [
          el('span', { class: 'sv-offer__label', text: s.offer.label }),
          el('span', { class: 'sv-offer__hint', text: (() => { try { return s.offer.hint ? s.offer.hint(r.snap, this.cat) : ''; } catch (_) { return ''; } })() }),
          el('span', { class: 'sv-offer__go', text: '→' }),
        ])),
      );
    }
    // The slot inspector (?slots=1): every slot's status, source and value.
    renderSlots(snap) {
      if (!this.ctx.debug) { this.$slots.replaceChildren(); return; }
      const show = (k, s) => {
        const v = s.value;
        if (v == null) return '—';
        if (k === 'decorations') return v.map((d) => `${d.location}:${d.design.kind}${d.design.inks ? `/${d.design.inks}ink` : ''}`).join(' ') || '[]';
        if (k === 'sizes') return Object.entries(v).map(([a, b]) => `${a}${b}`).join(' ') || '{}';
        if (k === 'style') return String(v).replace(/-custom$/, '').split('-').slice(0, 3).join('-');
        if (k === 'contact') return [v.email, v.sms].filter(Boolean).join(' ');
        return String(v);
      };
      const im = snap.brief.image;
      const upload = im ? [el('span', { class: `sv-slot sv-slot--${im.source === 'shopper' ? 'confirmed' : 'proposed'}`, title: 'brief image: classifier result and policy' }, [el('b', { text: 'upload' }), el('span', { text: `${im.kind}${im.asIs ? ' · as is' : ' · draw art'}` }), el('i', { text: im.source || 'unclassified' })])] : [];
      this.$slots.replaceChildren(...upload, ...C.SLOTS.map((k) => {
        const s = snap.slots[k], e = snap.effective[k];
        const status = s.status, src = C.filled(s) ? s.source : 'default';
        return el('span', { class: `sv-slot sv-slot--${status}`, title: `${k}: ${status}, source ${src}` }, [
          el('b', { text: k }), el('span', { text: show(k, C.filled(s) ? s : e) }), el('i', { text: `${status === 'empty' ? 'empty→default' : status} · ${src}` }),
        ]);
      }));
    }

    // ------------------------------------------------------------------ emissions
    emit(e) {
      if (!e || !e.type) return;
      const from = this.state.side || (this.route && this.route.core && this.route.core.id);
      // A quiet update (the sizes grid, 2026-10-19 revamp Phase 2): applied through the rules but not re-rendered, so the
      // shopper keeps typing in the grid, which paints its own totals. Returns what the rules did (e.g. a stock clamp).
      if (e.type === 'updates' && e.quiet && !this.added) {
        const r = C.apply(this.state, e.updates || [], this.cat);
        this.state = { ...r.state, side: this.state.side };
        if (r.applied.some((u) => u.slot === 'sizes')) {
          const q = C.quote(C.effective(this.state, this.cat), this.cat);
          if (q) track('studio2_sizes_changed', this.props({ from, where: 'grid', pieces: q.pieces, sizes: q.lines.length, tier_min: q.fees[0] ? q.fees[0].fee.min : null, fee: q.fees[0] ? q.fees[0].fee.price / 100 : null, unit: q.unit / 100, total: q.total / 100 }));
        }
        const msg = [...new Set(r.rejected.map((x) => x.reason).concat(r.notes))];
        if (msg.length) this.toast(msg.join(' '));
        return r;
      }
      if (e.type === 'updates') {
        const r = C.apply(this.state, e.updates || [], this.cat);
        this.state = { ...r.state, side: null, brief: e.brief || this.state.brief };
        if (from === 'proposals' && r.applied.some((u) => u.slot === 'decorations')) {
          const pr = (this.state.proposals || []).find((p) => p.style === this.state.slots.style.value && p.colorway === this.state.slots.colorway.value);
          this.pickedTitle = pr ? pr.title : null;
        }
        track('studio2_emitted', this.props({ from, type: 'updates', slots: r.applied.map((u) => u.slot), rejected: r.rejected.map((x) => x.reason) }));
        // Sizes and quantity (2026-10-19 revamp measurement): pieces, the fee tier they reach and the price per piece.
        if (r.applied.some((u) => u.slot === 'sizes' || u.slot === 'method')) {
          const q = C.quote(C.effective(this.state, this.cat), this.cat);
          if (q) track('studio2_sizes_changed', this.props({ from, pieces: q.pieces, sizes: q.lines.length, tier_min: q.fees[0] ? q.fees[0].fee.min : null, fee: q.fees[0] ? q.fees[0].fee.price / 100 : null, unit: q.unit / 100, total: q.total / 100 }));
        }
        const msg = [...new Set(r.rejected.map((x) => x.reason).concat(r.notes))];
        if (msg.length) this.toast(msg.join(' '));
        if (this.added) this.added = null;
      } else if (e.type === 'proposals') {
        this.state = { ...this.state, proposals: e.proposals && e.proposals.length ? e.proposals : null, read: e.read || '', brief: e.brief || this.state.brief, side: null };
        track('studio2_emitted', this.props({ from, type: 'proposals', count: (e.proposals || []).length }));
        if (this.state.proposals) this.drawArt();
      } else if (e.type === 'handoff') {
        track('studio2_emitted', this.props({ from, type: 'handoff', to: e.to, screen: e.screen }));
        if (e.to === 'core') this.state = { ...this.state, side: null };
        else if (e.to === 'screen') {
          const s = C.screen(e.screen);
          if (s && s.kind === 'side') this.state = { ...this.state, side: s.id };
        } else if (e.to === 'checkout' || e.to === 'cart') {
          this.state = { ...this.state, side: null };
          this.checkout(e.to);
        }
      }
      this.render();
    }
    toast(msg) {
      const t = this.querySelector('[data-toast]');
      t.textContent = msg; t.hidden = false;
      clearTimeout(this._toast);
      this._toast = setTimeout(() => (t.hidden = true), 6000);
    }

    // ------------------------------------------------------------------ mockups
    mock(box, p, color, decs, opts = {}) {
      if (!p) return;
      box.classList.add('is-loading');
      const token = (box._tok = (box._tok || 0) + 1);
      // opts.closeup: the print large, with the whole garment as a small inset (idea cards).
      const make = opts.closeup
        ? Promise.all([R.closeup(p, color, decs, { size: opts.width || 640 }), R.mockup(p, color, decs, { width: 360 })])
        : R.mockup(p, color, decs, opts).then((c) => [c]);
      // A design that did not draw (bug 16, sweep 2026-10-21): an error with "Try again" in place of a blank garment, and
      // box.dataset.mockFailed, which the screens' main buttons and checkout() read, so nothing goes on with a blank mockup.
      const fail = (msg) => {
        box.classList.remove('is-loading');
        box.dataset.mockFailed = '1';
        box.replaceChildren(el('div', { class: 'sv-mockfail', role: 'alert' }, [
          el('p', { class: 'sv-error', text: msg }),
          el('button', { type: 'button', class: 'sv-btn', text: 'Try again', onclick: () => this.mock(box, p, color, decs, opts) }),
        ]));
        box.dispatchEvent(new CustomEvent('sv-mock', { bubbles: true, detail: { ok: false } }));
        track('studio2_mock_failed', this.props({ error: String(msg).slice(0, 120) }));
      };
      make.then(([c, inset]) => {
        if (box._tok !== token) return;
        if (c.missing || (inset && inset.missing)) return fail('Your design did not load on the garment. Try again, or go back and add the file again.');
        box.classList.remove('is-loading');
        delete box.dataset.mockFailed;
        box.dispatchEvent(new CustomEvent('sv-mock', { bubbles: true, detail: { ok: true } }));
        c.className = 'sv-mock';
        c.setAttribute('role', 'img');
        c.setAttribute('aria-label', `${p.title} in ${color}${opts.closeup ? ', close-up of the print' : ''}`);
        const kids = [c];
        if (inset) { inset.className = 'sv-mock sv-mock--inset'; inset.setAttribute('aria-hidden', 'true'); kids.push(inset); }
        box.replaceChildren(...kids);
      }).catch((err) => { if (box._tok === token) fail(err.message); });
    }
    // Every design on the order draws (bug 16): a file the browser cannot open never reaches the cart or a group.
    async designsDraw(p, color, decs) {
      if (this.querySelector('.sv-screen [data-mock-failed]')) throw Object.assign(new Error('Your design did not load on the garment. Tap “Try again” on the picture first.'), { user: true });
      const g = await R.garment(p, color);
      for (const d of decs) {
        if (!d.design || d.design.kind === 'text') continue;
        try { await R.drawable(d.design, g.rgb); } catch (_) {
          throw Object.assign(new Error('Your design did not load. Go back and add the file again, as a PNG or JPG.'), { user: true });
        }
      }
    }

    // ------------------------------------------------------------------ model shot (experiment studio-model-shot)
    // Test arm: the review screen also shows the design worn by a person, beside the flat mockup, tap to swap.
    // The person and scene are one of the style's AI model shots in the CMS (product metafield spec.portfolio,
    // the product page's "Worn" sheet); only AI shots, never the real photos also listed there; the AI Worker's /api/wear prints the shopper's design on that shot
    // (and recolours the garment when the colourway has no shot of its own). The flat mockup still defines
    // the print: it shows first, and the model shot carries the label "AI-rendered".
    // Eligible = the style has a shot without a print on it, and a decoration sits on a front print area.
    // Exposure (PostHog getFeatureFlag via mvVariant) happens only for eligible designs, in both arms.
    async wearPlan(p, color, decs) {
      // AI shots only (portfolio-*, ai not false): spec.portfolio also lists real photos of real people
      // (real-*, ai: false, a photographer's work), which are never edited.
      const shots = (Array.isArray(p.worn) ? p.worn : []).filter((f) => f && f.url && !f.decorated && f.ai !== false && /\/portfolio-[^/]+$/.test(f.url.split('?')[0]));
      // Placeholder lettering ("YOUR TEXT") is never drawn on a person: only the shopper's own content is.
      decs = (decs || []).filter((x) => !C.isPlaceholder(x.design));
      if (!shots.length || !decs.length) return null;
      const g = await R.garment(p, color);
      const d = decs.find((x) => { const z = g.zones.find((zz) => zz.key === x.location); return z && z.view === 'front'; });
      if (!d) return null;
      const z = g.zones.find((zz) => zz.key === d.location);
      const same = shots.find((f) => f.color === color);
      const side = d.location === 'LC' ? ", on the wearer's left chest" : d.location === 'RC' ? ", on the wearer's right chest" : '';
      const noun = { hood: 'hoodie', crew: 'crew-neck sweatshirt', tee: 't-shirt', tank: 'tank top', blanket: 'sweatshirt-fleece throw blanket' }[p.family] || 'garment';
      // The print's real size (what the flat mockup shows), not the print area's: a small upload prints small.
      const pi = await R.printInfo(p, color, d).catch(() => null);
      const size = pi && pi.inches ? `${pi.inches[0].toFixed(1)} inches wide and ${pi.inches[1].toFixed(1)} inches tall` : `about ${String(z.inches || '').replace(' x ', ' by ')} inches`;
      // Size as Cal asked (2026-10-15, "match the print size"): the print's share of the chest width, armpit to
      // armpit, as the flat mockup draws it, and where its top edge sits. (Sending the mockup image too made the
      // model draw prints smaller; see the Worker's wear().)
      const chestIn = (/youth/i.test(p.title) ? 0.8 : 1) * ({ tee: 20, tank: 18, hood: 22, crew: 22, layer: 22, blanket: 54 }[p.family] || 21);
      const pb = await R.printBox(p, color, d).catch(() => null);
      // Measured on the mockup's own lay-down when the body width can be read there; else printed inches over chestIn.
      const share = pb && pb.chest ? Math.min(1, pb.b.w / pb.chest) : pi && pi.inches ? Math.min(1, pi.inches[0] / chestIn) : null;
      const top = pb ? Math.max(0, Math.min(1, (pb.b.cy - pb.b.h / 2 - pb.top) / pb.H)) : null;
      return { share, top, shot: (same || shots[0]).url, recolor: !same, d, g, garment: `${p.title.replace(/\s*-?\s*custom$/i, '')} (${noun})`, location: `in the ${z.label} print area${side}, printed ${size}` };
    }
    // A design's identity by content: the rules layer may hand the screen a copy of the same design on a re-render.
    designId(D) {
      if (D.kind === 'text') return `t:${D.text}|${D.font}|${D.ink}|${D.arc ? 1 : 0}|${D.layout || ''}|${D.title || ''}`;
      const src = String(D.src || D.name || '');
      return `${D.kind}:${src.length}:${src.slice(-64)}`;
    }
    wearImage(p, color, plan) {
      const k = `${p.handle}|${color}|${plan.d.location}|${this.designId(plan.d.design)}`;
      const cache = (this.memo.wear = this.memo.wear || {});
      if (cache[k]) return cache[k];
      const made = Object.keys(cache).length;
      if (made >= WEAR_LIMIT) return null; // a few per visit: each one is an image-model call
      const t0 = Date.now();
      cache[k] = (async () => {
        const flat = await R.flatOnKey(plan.d.design, plan.g.rgb);
        // As a JPEG of at most 768 px: the flat PNG is often over 1 MB, and the model needs no more.
        const image = (await smallDataUrl(flat.dataUrl, 768)) || flat.dataUrl;
        const body = { share: plan.share, top: plan.top, shot: plan.shot, image, key: flat.key, garment: plan.garment, location: plan.location, color: plan.recolor ? `${color} (${plan.g.hex})` : color, recolor: plan.recolor, sid: sid() };
        const r = await fetch(this.aiUrl('/api/wear'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const j = await r.json().catch(() => ({}));
        if (j.limit) { track('studio2_ai_cap_reached', this.props({ route: 'wear', used: j.used })); throw Object.assign(new Error(j.error), { user: true }); }
        if (!r.ok || !j.image) throw new Error(j.error || `wear ${r.status}`);
        track('studio2_model_shot_shown', this.props({ ms: Date.now() - t0, recolor: plan.recolor, n: made + 1 }));
        return j.image;
      })();
      cache[k].catch((err) => { track('studio2_model_shot_failed', this.props({ error: String(err.message || err).slice(0, 200), ms: Date.now() - t0 })); });
      return cache[k];
    }
    // Opt-in since 2026-11-02 (Cal: cut the image bill): the test arm shows a "See it on a person" button, and the
    // model shot is drawn only when the shopper taps it. Before, every test-arm review screen drew one at once.
    // A shot already drawn this visit (same garment, colour and design) shows again without a new call.
    async wearView(stage, mockBox, p, color, decs) {
      let plan = null;
      try { plan = await this.wearPlan(p, color, decs); } catch (_) { return; }
      if (!plan || !this.aiUrl('')) return;
      const variant = await new Promise((res) => (window.mvVariant ? window.mvVariant('studio-model-shot', res) : res('control')));
      if (!this.memo.wearExposed) { this.memo.wearExposed = true; track('studio2_model_shot_exposure', this.props({ variant, recolor: plan.recolor })); }
      if (variant !== 'test' || !stage.isConnected) return;
      const wk = `${p.handle}|${color}|${plan.d.location}|${this.designId(plan.d.design)}`;
      const made = (this.memo.wear || {})[wk];
      if (!made && Object.keys(this.memo.wear || {}).length >= WEAR_LIMIT) return;
      const tag = el('span', { class: 'sv-wear__tag', text: 'AI-rendered' });
      const bigImg = el('img', { alt: `A person wearing the ${p.title} in ${color} with your design (AI-rendered)` });
      const big = el('div', { class: 'sv-wear__big' }, [bigImg, tag]);
      const chipPic = el('span', { class: 'sv-wear__pic sv-wear__pic--ask', 'aria-hidden': 'true' });
      const chipTxt = el('span', { class: 'sv-wear__label', text: 'See it on a person' });
      const chip = el('button', { type: 'button', class: 'sv-wear__chip', 'aria-pressed': 'false' }, [chipPic, chipTxt]);
      let ready = false;
      const show = (worn, how) => {
        stage.classList.toggle('is-worn', worn);
        chip.setAttribute('aria-pressed', String(worn));
        this.memo.wearView = worn ? 'worn' : 'flat';
        const flat = mockBox.querySelector('canvas');
        chipPic.replaceChildren(worn ? (flat ? el('img', { src: flat.toDataURL('image/jpeg', 0.7), alt: '' }) : '') : el('img', { src: bigImg.src, alt: '' }));
        chipTxt.textContent = worn ? 'Flat mockup' : 'On a person';
        if (how) track('studio2_model_shot_swapped', this.props({ to: worn ? 'worn' : 'flat', how }));
      };
      const load = async (how) => {
        const job = this.wearImage(p, color, plan);
        if (!job) { chip.remove(); return; }
        chip.disabled = true; chipPic.classList.remove('sv-wear__pic--ask'); chipPic.classList.add('sv-wear__pic--wait'); chipTxt.textContent = 'On a person…';
        if (how) track('studio2_model_shot_requested', this.props({ recolor: plan.recolor }));
        try {
          bigImg.src = await job;
          await bigImg.decode().catch(() => {});
          ready = true; chip.disabled = false; chipPic.classList.remove('sv-wear__pic--wait');
          show(how ? true : this.memo.wearView === 'worn');
        } catch (err) {
          if (err && err.user) this.toast(err.message);
          chip.remove(); big.remove(); stage.classList.remove('has-wear');
        }
      };
      chip.addEventListener('click', () => { if (ready) show(!stage.classList.contains('is-worn'), 'tap'); else if (!chip.disabled) load('tap'); });
      stage.classList.add('has-wear');
      stage.append(big, chip);
      if (made) load(null);
    }

    // ------------------------------------------------------------------ AI
    // GET /api/ship on the AI Worker: {known, city, region, zone, rate, freeOver, arrivesBy, ...} for the
    // visitor's IP location. Kept for the tab (same day) so the cart page and later visits reuse it.
    async loadShip() {
      try {
        // Redesign part 3: one date for every surface (assets/mv-delivery.js: the daily cutoff and the shopper's saved ZIP).
        if (window.mvShip) {
          if (!this._zipWatch) { this._zipWatch = true; addEventListener('mv:zip', () => this.loadShip()); }
          this.ship = await window.mvShip();
          if (this.route && this.route.core && this.route.core.id === 'review') this.render();
          else this.paintArrives();
          return;
        }
        const today = new Date().toDateString();
        const c = JSON.parse(sessionStorage.getItem('mv_ship2') || 'null');
        if (c && c.day === today) { this.ship = c.v; return; }
        const u = this.aiUrl('/api/ship');
        if (!u) return;
        const r = await fetch(u);
        if (!r.ok) return;
        this.ship = await r.json();
        try { sessionStorage.setItem('mv_ship2', JSON.stringify({ day: today, v: this.ship })); } catch (_) {}
        if (this.route && this.route.core && this.route.core.id === 'review') this.render();
        else this.paintArrives();
      } catch (_) { /* the work order falls back to "shipping shown at checkout" */ }
    }
    async loadCart() {
      try { const c = await (await fetch('/cart.js')).json(); this.cartCents = c.total_price || 0; this.cartGrams = c.total_weight || 0; if (window.mvCartSeen) window.mvCartSeen(c); return c; } catch (_) { return null; }
    }
    aiUrl(path) { const e = aiOverride || this.d.aiEndpoint; return e ? e.replace(/\/$/, '') + path : null; }
    // A compact catalog for the model: handle, title, family, colorways, locations, price.
    aiCatalog() {
      const sl = this.state.slots;
      const fixed = C.confirmed(sl.style) ? sl.style.value : null;
      return this.cat.products.filter((p) => !fixed || p.handle === fixed).map((p) => ({ h: p.handle, t: p.title, f: p.family, colors: C.availableColors(p) /* in stock only */, locs: p.zoneKeys.filter((k) => /^(FC|FCZC|LC|FB|BKT|BCT)$/.test(k)).concat(p.zoneKeys.filter((k) => !/^(FC|FCZC|LC|FB|BKT|BCT)$/.test(k)).slice(0, 2)), price: Math.round(p.minPrice / 100) }));
    }
    // What is this upload? → { kind, printableAsIs, confidence, subject, text, source: 'ai' }, or null
    // when the classifier is unavailable (the policy then treats the image as a logo).
    async classify(image) {
      const t0 = Date.now();
      // The image could not be read on this device: say so (stage "read"), do not call the AI with nothing.
      const readable = async (im) => { const u = await smallDataUrl(im.src, 640, im.file); if (!u) throw Object.assign(new Error('image could not be read on this device'), { stage: 'read' }); return u; };
      // At most 8 s (sweep 2026-10-21 bug 11): in the Instagram app one check hung 55 s while "Place my logo" waited on it;
      // the other 13 took 1.7 to 3 s. A check that runs out returns null, and the door's own answer (a logo) stands.
      const ac = window.AbortController ? new AbortController() : null, cut = ac && setTimeout(() => ac.abort(), 8000);
      try {
        const r = await fetch(this.aiUrl('/api/classify'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image: await readable(image), sid: sid() }), signal: ac ? ac.signal : undefined });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.kind) throw new Error(j.error || `classify ${r.status}`);
        // banned: a hate or criminal mark (AI Worker, revamp Phase 2 defect 4): the brief screen refuses the image.
        const c = { kind: j.kind, printableAsIs: !!j.printableAsIs, confidence: j.confidence, subject: j.subject || '', text: j.text || '', source: 'ai', banned: j.banned === true, bannedWhy: j.bannedWhy || '' };
        track('studio2_image_classified', this.props({ kind: c.kind, as_is: C.imagePolicy(c).asIs, confidence: c.confidence, ms: Date.now() - t0 }));
        return c;
      } catch (err) {
        console.warn('[studio v2] classify failed', err);
        // kind "unknown" is the Studio's own label for a failed classification, never a model answer.
        track('studio2_image_classified', this.props({ kind: 'unknown', failed: true, stage: err.stage || (err.name === 'AbortError' ? 'timeout' : 'ai'), error: String(err.message || err).slice(0, 120), ms: Date.now() - t0 }));
        return null;
      } finally { if (cut) clearTimeout(cut); }
    }
    async propose({ text, image }) {
      const t0 = Date.now(), sl = this.state.slots;
      const fixed = { style: C.confirmed(sl.style) ? sl.style.value : undefined, colorway: C.confirmed(sl.colorway) ? sl.colorway.value : undefined };
      let raw = null, read = '', via = 'ai', caption = '', ai = {};
      try {
        if (!this.aiUrl('')) throw new Error('no AI endpoint');
        const body = { brief: text, catalog: this.aiCatalog(), fixed, n: 3, sid: sid() };
        if (image) {
          body.image = await smallDataUrl(image.src, 768, image.file);
          const pol = C.imagePolicy(image.cls);
          body.imageClass = { kind: pol.kind, asIs: pol.asIs, subject: pol.subject, text: pol.text };
        }
        const r = await fetch(this.aiUrl('/api/propose'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `AI ${r.status}`);
        raw = j.proposals; read = j.read || ''; caption = j.caption || ''; ai = j;
      } catch (err) {
        console.warn('[studio v2] proposals fell back', err);
        via = 'fallback';
      }
      // The policy is enforced here too, whatever the model returned: a photo is never put on a
      // garment directly by a proposal.
      const policy = image ? C.imagePolicy(image.cls) : null;
      let proposals = (raw || []).map((x) => C.applyImagePolicy(this.normalize(x, text, image), policy)).filter(Boolean);
      if (image) proposals = C.enforceAsIs(proposals, policy, { kind: 'upload', src: image.src, file: image.file, name: image.name });
      if (proposals.length < 2) { proposals = proposals.concat(this.fallback(text, image)).slice(0, 3); if (via === 'ai') via = 'ai+fallback'; }
      // The shopper's caption on every idea (Cal, 2026-10-15), and the upload class the pick-rate table counts by.
      proposals = C.applyCaption(proposals, caption);
      this.uploadClass = ai.uploadClass || (image ? 'other' : 'none');
      proposals = proposals.map((p) => ({ ...p, uploadClass: this.uploadClass }));
      // Meta topic events (posthog.liquid toMeta): what the design is about, from photo, words, AI read and idea titles.
      const topics = C.topics([policy && policy.subject, policy && policy.text, text, read, ...(raw || []).map((x) => x && x.title)]);
      track('studio2_proposals', this.props({ ms: Date.now() - t0, via, topics, image_kind: policy ? policy.kind : null, image_as_is: policy ? policy.asIs : null, count: proposals.length, kinds: proposals.map((p) => p.decorations[0].design.kind), has_image: !!image, words: text.split(/\s+/).filter(Boolean).length,
        art_styles: proposals.map((p) => C.artStyle(p.decorations[0].design)), upload_class: this.uploadClass, caption: !!caption, brief_kind: ai.briefKind || null }));
      return { proposals, read };
    }
    // AI output → a proposal the rules accept: real handle, colorway, location; a design.
    normalize(x, text, image) {
      const fixedStyle = C.confirmed(this.state.slots.style) ? this.state.slots.style.value : null;
      const p = C.product(this.cat, fixedStyle || x.style) || this.cat.products.find((q) => q.family === (C.ALIASES[String(x.style || '').toLowerCase()] || '')) || null;
      if (!p) return null;
      const fixedColor = C.confirmed(this.state.slots.colorway) ? this.state.slots.colorway.value : null;
      const colorway = [fixedColor, x.colorway].find((c) => c && C.colorAvailable(p, c)) || C.defaultColor(p);
      const location = C.mapLocation(p, String(x.location || 'FC').toUpperCase());
      let design;
      if (x.kind === 'upload' && image) design = { kind: 'upload', src: image.src, file: image.file, name: image.name };
      else if (x.kind === 'art' && x.artPrompt) design = { kind: 'art', prompt: x.artPrompt, src: null, key: '#00ff00', pending: true, fromImage: !!image, fromPhoto: !!image && !C.imagePolicy(image.cls).asIs, styleKey: x.styleKey || null, caption: x.caption || '', placement: x.captionPlacement || null, lettering: x.letteringStyle || null };
      else {
        const words = C.cleanLettering(x.text || text, text) || C.cleanLettering(String(x.title || '').toUpperCase(), text);
        design = words ? { kind: 'text', text: words, font: R.FONTS[x.font] ? x.font : 'graduate', ink: R.INKS[x.ink] ? x.ink : 'White', inks: 1 } : C.placeholderDesign();
      }
      return { id: rid('P'), title: String(x.title || 'An idea').slice(0, 60), pitch: String(x.pitch || '').slice(0, 160), style: p.handle, colorway, decorations: [{ location, design }] };
    }
    // No AI (or too few ideas): three honest starting points across garment families. The design is the shopper's
    // words, their image as it is, or art drawn from their photo; never "YOUR TEXT" when there is any of those
    // (C.fallbackDesign). Art goes on the full chest.
    fallback(text, image) {
      const fams = C.confirmed(this.state.slots.style) ? [null, null, null] : ['hood', 'tee', 'blanket'];
      return fams.map((f, i) => {
        const p = f ? this.cat.products.filter((q) => q.family === f && !/youth/i.test(q.title)).sort((a, b) => a.minPrice - b.minPrice)[0] : C.product(this.cat, this.state.slots.style.value);
        if (!p) return null;
        const design = C.fallbackDesign(text, image, i);
        const small = i === 1 && design.kind !== 'art';
        const loc = C.mapLocation(p, small ? 'LC' : 'FC');
        const title = `${p.title}${design.kind === 'upload' ? ' with your art' : design.kind === 'art' ? ' with art from your photo' : ''}`;
        const pitch = design.placeholder ? 'Add your words on the next step.' : small ? 'Small and clean on the chest.' : 'Big and bold, front and centre.';
        return { id: rid('P'), title, pitch, style: p.handle, colorway: C.defaultColor(p), decorations: [{ location: loc, design }] };
      }).filter(Boolean);
    }
    // "Work on the design": the design flattened on a key + the shopper's words → a new art design.
    // `caption`: the words the edit leaves in the design (Edit the design: change or remove the caption).
    async editArt({ design, instruction, product, color, caption }) {
      const t0 = Date.now();
      const g = await R.garment(product, color);
      const flat = await R.flatOnKey(design, g.rgb);
      const r = await fetch(this.aiUrl('/api/edit'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ image: flat.dataUrl, instruction, key: flat.key, garment: g.hex, sid: sid() }) });
      const j = await r.json().catch(() => ({}));
      if (j.limit) track('studio2_ai_cap_reached', this.props({ route: 'edit', used: j.used }));
      if (!r.ok || !j.image) { track('studio2_design_edit_failed', this.props({ error: String(j.error || r.status).slice(0, 120) })); throw new Error(j.error || 'The AI could not change it. Try saying it another way.'); }
      track('studio2_design_edited', this.props({ ms: Date.now() - t0, words: instruction.split(/\s+/).length, from: design.kind }));
      const out = { kind: 'art', prompt: `${design.prompt ? design.prompt + ' — ' : ''}edited: ${instruction}`, src: j.image, key: flat.key, edited: true, fromPhoto: !!design.fromPhoto, styleKey: design.styleKey || null, caption: caption !== undefined ? caption : design.kind === 'text' ? '' : design.caption || '' };
      const p = product, gg = await R.garment(p, color);
      out.inks = await R.inkCount(out, gg.rgb, this.cat.inks).catch(() => null);
      return out;
    }
    // Draw every pending "art" design (in parallel), then re-render the proposals screen.
    drawArt() {
      const props = this.state.proposals || [];
      const brief = this.state.brief;
      const rerender = () => { if (this.route && this.route.core && this.route.core.id === 'proposals' && !this.state.side) this.render(); };
      props.forEach((pr) => pr.decorations.forEach(async (d) => {
        const D = d.design;
        try {
          if (D.kind === 'art' && !D.src) {
            const p = C.product(this.cat, pr.style);
            const g = await R.garment(p, pr.colorway).catch(() => null);
            // keyAuto: the Worker picks the background key the art will not use and says which (j.key), so a green
            // leprechaun is not drawn on green (revamp Phase 2 defect 1). allowed: words in the shopper's image may be drawn.
            const body = { prompt: D.prompt, key: D.key, keyAuto: true, garment: g ? g.hex : '#1d2a3a', sid: sid(), allowed: brief.image && brief.image.cls && brief.image.cls.text ? [brief.image.cls.text] : [] };
            if (D.caption) Object.assign(body, { caption: D.caption, placement: D.placement, lettering: D.lettering });
            if (D.fromImage && brief.image) { body.image = await smallDataUrl(brief.image.src, 1024, brief.image.file); body.fromPhoto = !!D.fromPhoto; }
            const t0 = Date.now();
            // Three tries, a moment apart, each cut off after 75 s: a failed picture used to leave the card printing the
            // idea's title (2026-10-11), and in the Instagram app a dropped request ("Load failed", sweep 2026-10-21 bug 12)
            // left one card on "Drawing your art…" for a minute. The card says when it is drawing again (D.retry).
            for (let attempt = 0; ; attempt++) {
              const ac = window.AbortController ? new AbortController() : null, cut = ac && setTimeout(() => ac.abort(), 75000);
              try {
                const r = await fetch(this.aiUrl('/api/art'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ac ? ac.signal : undefined });
                const j = await r.json().catch(() => ({}));
                // The shopper's daily AI image cap (AI Worker src/cap.ts): no retry, one toast, the idea is dropped below.
                if (j.limit) { attempt = 2; if (!this.memo.capToast) { this.memo.capToast = true; this.toast(j.error); track('studio2_ai_cap_reached', this.props({ route: 'art', used: j.used })); } }
                if (!r.ok || !j.image) throw new Error(j.error || `art ${r.status}`);
                D.src = j.image; D.pending = false; D.retry = false; if (/^#[0-9a-fA-F]{6}$/.test(j.key || '')) D.key = j.key;
                break;
              } catch (err) {
                track('studio2_art_failed', this.props({ error: String(err.message || err).slice(0, 200), attempt, final: attempt >= 2 }));
                if (attempt >= 2) throw err;
                D.retry = true; rerender();
                await new Promise((res) => setTimeout(res, 1200 * (attempt + 1)));
              } finally { if (cut) clearTimeout(cut); }
            }
            track('studio2_art_drawn', this.props({ ms: Date.now() - t0 }));
          }
          if (D.kind !== 'text' && D.src && !D.inks) {
            const p = C.product(this.cat, pr.style), g = await R.garment(p, pr.colorway);
            // A design that does not show on its garment (dark line art on a black hood) moves to the
            // in-stock colourway it reads best on, unless the shopper fixed the colour.
            if (!C.confirmed(this.state.slots.colorway)) {
              const score = await R.readability(D, g.rgb).catch(() => 21);
              if (score < 2) {
                let best = { name: pr.colorway, s: score };
                for (const name of C.availableColors(p).slice(0, 12)) {
                  if (name === pr.colorway) continue;
                  const gg = await R.garment(p, name).catch(() => null);
                  const s2 = gg ? await R.readability(D, gg.rgb).catch(() => 0) : 0;
                  if (s2 > best.s) best = { name, s: s2 };
                }
                if (best.name !== pr.colorway) { track('studio2_contrast_fixed', this.props({ from: pr.colorway, to: best.name, before: +score.toFixed(2), after: +best.s.toFixed(2) })); pr.colorway = best.name; }
              }
            }
            const g2 = await R.garment(p, pr.colorway);
            D.inks = await R.inkCount(D, g2.rgb, this.cat.inks).catch(() => 3);
          }
        } catch (err) {
          console.warn('[studio v2] art failed', err);
          // Drop the idea rather than print its title as the design. Only when no other idea is
          // left does it become lettering, from the shopper's own words where there are any.
          const rest = (this.state.proposals || []).filter((x) => x !== pr);
          if (this.state.proposals && rest.length && this.state.proposals.includes(pr)) this.state = { ...this.state, proposals: rest };
          else {
            // The shopper's own words, or a placeholder the Studio asks them to fill (never the idea's title).
            // The idea's caption when it has one ("Pin Pals"), not the whole brief ("vintage art of a beagle ... Pin Pals").
            const words = C.cleanLettering(D.caption || (brief && brief.text), brief && brief.text);
            Object.assign(D, words ? { kind: 'text', text: words, font: 'graduate', ink: 'White', inks: 1 } : C.placeholderDesign(), { pending: false, src: null });
            pr.title = String(pr.title || '').replace(/ with art from your photo$/, words ? ' with your words' : '');
            if (!words) pr.pitch = 'Add your words on the next step.';
          }
        }
        rerender();
      }));
    }

    // ------------------------------------------------------------------ cart hand-off
    uploadUrl() { return this.d.uploadEndpoint || null; }
    store(blob, name) {
      const url = this.uploadUrl();
      if (!blob || !url) return Promise.reject(new Error('no upload endpoint'));
      let p = this.uploads.get(blob);
      if (p) return p;
      p = (async () => {
        for (let i = 0; ; i++) {
          try {
            const r = await fetch(`${url}?name=${encodeURIComponent(name || 'file')}`, { method: 'POST', headers: { 'Content-Type': blob.type || 'application/octet-stream' }, body: blob });
            const j = await r.json().catch(() => ({}));
            if (r.ok && j.url) return j.url;
            throw new Error(j.error || `upload ${r.status}`);
          } catch (err) { if (i >= 1) throw err; await new Promise((res) => setTimeout(res, 800)); }
        }
      })();
      this.uploads.set(blob, p);
      p.catch(() => this.uploads.delete(blob));
      return p;
    }
    async post(url, body) {
      const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.status) throw new Error(j.description || j.message || `Cart request failed (${r.status})`);
      return j;
    }
    async cartClash(sample, variantId) {
      let cart;
      try { cart = await (await fetch('/cart.js')).json(); } catch (_) { return ''; }
      const orders = new Map();
      cart.items.forEach((i) => { const p = i.properties || {}; if (!p._order) return; const o = orders.get(p._order) || { sample: !!p._sample, variants: [] }; if (p._role === 'blank') o.variants.push(i.variant_id); orders.set(p._order, o); });
      const all = [...orders.values()], samples = all.filter((o) => o.sample);
      if (!sample) return samples.length ? 'Your cart has a sample in it. A sample checks out on its own: check out the sample first, or remove it, then add this order.' : '';
      if (samples.length < all.length) return 'Your cart has a full order in it. A sample checks out on its own: check out the full order first, or remove it, then add the sample.';
      if (samples.length >= SAMPLE_LIMIT) return `Your cart already has ${SAMPLE_LIMIT} samples, the most for one order.`;
      if (samples.some((o) => o.variants.includes(variantId))) return 'A sample of this garment and size is already in your cart. One sample of each.';
      return '';
    }
    // The last look before the cart (revamp Phase 2 defect 4): every design and its words go to the AI Worker's /api/screen;
    // a hate or criminal mark is refused here even when the upload check was skipped or failed. When the check itself
    // cannot run, the order goes on (the art check by staff still sees every order).
    async screenDesigns(decs) {
      const words = decs.filter((d) => d.design.kind === 'text').map((d) => `${d.design.title ? d.design.title + ' / ' : ''}${String(d.design.text || '').replace(/\n/g, ' / ')}`).concat(decs.map((d) => d.design.caption || '')).concat([this.state.brief.text || '']).filter(Boolean).join(' / ').slice(0, 400);
      const imgs = decs.filter((d) => d.design.kind !== 'text' && d.design.src);
      const ask = async (body) => {
        try {
          const r = await fetch(this.aiUrl('/api/screen'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, sid: sid() }) });
          const j = await r.json().catch(() => ({}));
          return r.ok ? j : null;
        } catch (_) { return null; }
      };
      const checks = await Promise.all([...imgs.map(async (d) => ask({ image: await smallDataUrl(d.design.src, 768, d.design.file), text: words })), imgs.length ? null : words ? ask({ text: words }) : null]);
      const hit = checks.find((c) => c && c.banned);
      if (hit) {
        track('studio2_checkout_blocked', this.props({ gates: ['banned'], why: String(hit.why || '').slice(0, 80) }));
        throw Object.assign(new Error(hit.message || "We can't print this design. Try your own logo or other words."), { user: true });
      }
    }
    describe(p, color, d, g) {
      const z = g.zones.find((x) => x.key === d.location);
      const D = d.design;
      // A roster (Phase 3): the same list on every piece, every name written out for the print lane.
      const roster = D.layout === 'roster' ? C.parseRoster(D.text) : null;
      const pcs = D.layout === 'pieces' ? D.pieces || [] : null;
      const art = pcs ? `Name and number, each piece its own (${pcs.length} pieces) — ${(R.FONTS[D.font] || R.FONTS.graduate).name}, ${R.inkFor(D.ink, g.rgb)}. Each garment line carries its own print file (_piece_art). ${pcs.map((x) => `${x.size} ${[x.name, x.number].filter(Boolean).join(' ')}`).join(', ')}`.slice(0, 1500)
        : roster ? `Roster, the same on every piece${D.title ? `, heading “${D.title}”` : ''}: ${roster.length} names${roster.some((x) => x.number) ? ' with numbers' : ''} — ${(R.FONTS[D.font] || R.FONTS.graduate).name}, ${R.inkFor(D.ink, g.rgb)}: ${roster.map((x) => [x.number, x.name].filter(Boolean).join(' ')).join(', ')}`.slice(0, 1500)
        : D.kind === 'text' ? `Text “${String(D.text).replace(/\n|\\n/g, ' / ')}” — ${(R.FONTS[D.font] || R.FONTS.graduate).name}, ${R.inkFor(D.ink, g.rgb)}${D.arc ? ', first line arched' : ''}`
        : D.kind === 'art' ? `AI art (Studio v2)${D.caption ? ` with the words “${D.caption}”` : ''}: ${String(D.prompt || '').slice(0, 200)}`
        : D.asIs ? `Logo printed as it is (no AI)${D.cleaned ? ', the shopper chose the cleaned-up copy (background removed)' : ', from the shopper\'s file'}: ${D.name || 'upload'}` : `Image ${D.name || 'upload'}`;
      return { label: z ? z.label : d.location, size: z ? `print area ${z.size[0]} × ${z.size[1]} in` : '', art };
    }
    // ------------------------------------------------------------------ group link (Phase 3 item 5)
    groupsUrl(path) { return GROUPS + path; }
    // Make a group from this design: the files and line properties the Studio cart would carry, uploaded now, plus the
    // garment's variants and the fee breaks, so the back end can build the group's Shopify order at any count.
    // o: { title, deadline (ISO), names (each person adds a name and number on the back), payMode, expected, organiser,
    //      own (organiser pays: the sizes in the grid, or the design's own pieces, become the organiser's pieces) }.
    async createGroup(o) {
      const e0 = C.effective(this.state, this.cat), p = C.product(this.cat, e0.style.value), color = e0.colorway.value;
      let decs = (e0.decorations.value || []).map((d) => ({ location: d.location, design: d.design }));
      // Names and numbers in a group: each person types their own, so the back's box is fixed (R pieceCanvas box 'group').
      let pd = decs.find((d) => d.design.layout === 'pieces');
      const typed = pd ? pd.design.pieces.map((x) => ({ size: x.size, name: x.name || '', number: x.number || '' })) : null;
      const front = decs.find((d) => d.design.kind === 'text' && d !== pd);
      if (!pd && o.names && p.zoneKeys.includes('FB') && !decs.some((d) => d.location === 'FB')) {
        pd = { location: 'FB', design: { kind: 'text', layout: 'pieces', pieces: [{ size: C.defaultSize(p, color), name: 'YOUR NAME', number: '00' }], show: 0, font: (front && front.design.font) || 'graduate', ink: (front && front.design.ink) || 'White', inks: 1 } };
        decs.push(pd);
      }
      if (pd) {
        const hasName = pd.design.pieces.some((x) => x.name), hasNumber = pd.design.pieces.some((x) => x.number);
        pd.design = { ...pd.design, box: 'group', hasName, hasNumber, pieces: [{ size: C.defaultSize(p, color), name: hasName ? 'YOUR NAME' : '', number: hasNumber ? '00' : '' }], show: 0 };
      }
      const e = { ...e0, decorations: { ...e0.decorations, value: decs } }, q = C.quote(e, this.cat), t = q.type;
      if (!t) throw new Error(`${p.title} cannot be printed in full color.`);
      await this.designsDraw(p, color, decs);
      await this.screenDesigns(decs);
      const g = await R.garment(p, color);
      const id = rid('G');
      const mockCanvas = await R.mockup(p, color, decs);
      if (mockCanvas.missing) throw Object.assign(new Error('Your design did not load. Go back and add the file again, as a PNG or JPG.'), { user: true });
      const mockBlob = await R.toBlob(mockCanvas, 'image/jpeg', 0.85);
      const [mockUrl, ...locFiles] = await Promise.all([
        this.store(mockBlob, 'mockup.jpg'),
        ...decs.map(async (d) => ({
          art: await this.store(await R.printBlob(await R.artwork(p, color, d)), `art-${d.location}.png`),
          info: await R.printInfo(p, color, d).catch(() => null),
          upload: d.design.kind === 'upload' && d.design.file ? await this.store(d.design.file, d.design.name || 'upload').catch(() => null) : null,
        })),
      ]);
      const summary = decs.map((d) => this.describe(p, color, d, g).label).join(', ');
      const common = { 'Decorated order': id, Decoration: `${t.title}: ${summary}`, _order: id, _min: '1', _pieces: '1', Design: 'Customer designed (Studio v2), group link' };
      if (this.pickedTitle) common.Idea = this.pickedTitle;
      common['Art approval'] = this.memo.proof ? 'Yes — send me a proof first' : 'No';
      common._studio = 'v2';
      const how = String(q.fees[0] && q.fees[0].sv.label || 'full color').toLowerCase();
      const designName = o.title || this.pickedTitle || 'Your design';
      const plain = (props, visible) => Object.assign({}, visible, Object.fromEntries(Object.entries(props).map(([k, v]) => [k[0] === '_' ? k : `_${k}`, String(v)])));
      const fees = q.fees.map((x, i) => {
        const d = decs[i], info = this.describe(p, color, d, g);
        const props = { ...common, Location: `${info.label} (${d.location})`, Method: `${t.title} — ${x.sv.label} (${x.sv.code})`, 'Print size': info.size, Artwork: d === pd ? `Name and number, each person their own (group link) — ${(R.FONTS[pd.design.font] || R.FONTS.graduate).name}, ${R.inkFor(pd.design.ink, g.rgb)}. Each garment line carries its own print file (_piece_art).` : info.art, Garment: `${p.title} — ${color}`, _role: 'decoration', _location: d.location, _service: x.sv.code, _art: locFiles[i].art, _mockup: mockUrl };
        if (locFiles[i].upload) props._upload_1 = locFiles[i].upload;
        const pi = locFiles[i].info;
        if (pi && pi.inches && d !== pd) props['Design size'] = `${pi.inches[0].toFixed(1)} × ${pi.inches[1].toFixed(1)} in`;
        const check = artNotes(pi);
        if (check.length && d !== pd) props['Art check'] = check.join(' ');
        return { location: d.location, label: info.label, breaks: x.sv.breaks.map((b) => ({ min: b.min, id: b.id, price: b.price })), props: plain(props, { Design: designName, Print: `${info.label} · ${how}` }) };
      });
      const spots = decs.map((d) => this.describe(p, color, d, g).label).join(', ');
      const blankProps = plain({ ...common, _role: 'blank', _mockup: mockUrl }, { Design: designName, Print: `${spots} · ${how}` });
      const vs = p.variants.filter((v) => v.color == null || v.color === color);
      const spec = {
        product: { handle: p.handle, title: p.title }, color, sizes: p.sizes.filter((sz) => vs.some((v) => v.size === sz)),
        variants: vs.map((v) => ({ size: v.size, id: v.id, price: v.price, available: !!v.available })),
        fees, blankProps, orderAttrs: this.memo.proof ? { Proof: 'Send me a proof first', _proof: '1' } : {}, mockup: mockUrl,
        decoText: decs.map((d) => `${this.describe(p, color, d, g).label}: ${d === pd ? 'your name and number' : d.design.kind === 'text' ? `“${String(d.design.text).replace(/\n/g, ' / ')}”` : d.design.asIs ? 'the logo' : 'the design'}`).join(' · '),
        piece: pd ? { location: pd.location, font: pd.design.font, ink: pd.design.ink, name: pd.design.hasName, number: pd.design.hasNumber } : null,
      };
      // The organiser's own pieces: per-piece designs bring each piece with its own back file (the group's fixed box);
      // otherwise one piece per unit in the grid. A back newly added here has no names yet, so nothing comes in then.
      let own = [];
      if (o.ownPieces && (o.payMode || 'organiser') === 'organiser') {
        // The group flow's step 3 (group-first fork): the host's own pieces, typed there (with names and numbers when the
        // back has them, each with its own file in the group's fixed box).
        own = await Promise.all(o.ownPieces.slice(0, 200).map(async (x, i) => (pd ? { ...x, pieceArt: await this.store(await R.printBlob(await R.artwork(p, color, { location: pd.location, design: { ...pd.design, pieces: [{ size: x.size, name: pd.design.hasName ? String(x.name || '') : '', number: pd.design.hasNumber ? String(x.number || '').replace(/\D/g, '') : '' }], show: 0 } })), `group-own-${i + 1}-${pd.location}.png`) } : { size: x.size })));
      } else if (o.own && (o.payMode || 'organiser') === 'organiser') {
        if (typed) {
          own = await Promise.all(typed.slice(0, 200).map(async (x, i) => ({ ...x, pieceArt: await this.store(await R.printBlob(await R.artwork(p, color, { location: pd.location, design: { ...pd.design, pieces: [{ size: x.size, name: pd.design.hasName ? String(x.name) : '', number: pd.design.hasNumber ? String(x.number).replace(/\D/g, '') : '' }], show: 0 } })), `group-own-${i + 1}-${pd.location}.png`) })));
        } else if (!pd) {
          const sz = e0.sizes.value || {};
          own = spec.sizes.flatMap((s) => Array(Math.max(0, sz[s] || 0)).fill({ size: s })).slice(0, 200);
        }
      }
      const r = await fetch(this.groupsUrl('/api/groups'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title: o.title, deadline: o.deadline, payMode: o.payMode || 'organiser', expected: o.expected, organiser: o.organiser || {}, own, spec, emails: !!o.emails, internal: isInternal(), ph: phId() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.code) throw new Error(j.error || `The group link could not be made (${r.status}).`);
      if (window.StudioKeep) { this.kept = false; window.StudioKeep.clear(); } // the group page has its own link from here
      try { const mine = JSON.parse(localStorage.getItem('mv_groups') || '[]'); mine.unshift({ code: j.code, key: j.token, title: o.title, at: Date.now() }); localStorage.setItem('mv_groups', JSON.stringify(mine.slice(0, 20))); } catch (_) {}
      track('studio2_group_created', this.props({ code: j.code, pay: o.payMode || 'organiser', names: !!pd, own: own.length, flow: o.emails ? 'group-first' : 'sheet', quick: !!o.quick, deadline_days: Math.round((Date.parse(o.deadline) - Date.now()) / 86400000) }));
      return j;
    }
    async checkout(dest) {
      if (this.busy) return;
      const gates = C.gates(this.state, this.cat);
      if (gates.length) { this.toast(gates.map((g) => g.message).join(' ')); track('studio2_checkout_blocked', this.props({ gates: gates.map((g) => g.id) })); return; }
      const e = C.effective(this.state, this.cat), q = C.quote(e, this.cat), p = q.product, color = e.colorway.value, decs = e.decorations.value || [], sample = !!e.sample.value;
      this.busy = 'Saving your design…'; this.busyDest = dest; this.render();
      const t0 = Date.now();
      track('studio2_checkout_clicked', this.props({ dest, pieces: q.pieces, total: q.total / 100 }));
      try {
        const before = await this.loadCart();
        const clash = await this.cartClash(sample, q.lines[0] && q.lines[0].v.id);
        if (clash) throw Object.assign(new Error(clash), { user: true });
        await this.designsDraw(p, color, decs);
        await this.screenDesigns(decs);
        const g = await R.garment(p, color);
        const id = rid(sample ? 'S' : 'D');
        // Per-piece names and numbers (Phase 3 item 3): one print file per piece for that location, uploaded a few at a time.
        const pd = decs.find((d) => d.design.layout === 'pieces');
        let pieceFiles = [];
        if (pd) {
          const vFor = (size) => p.variants.find((v) => v.size === size && (v.color == null || v.color === color));
          const missing = pd.design.pieces.find((x) => !vFor(x.size) || !vFor(x.size).available);
          if (missing) throw Object.assign(new Error(`${p.title} in ${color} has no ${missing.size} in stock. Edit names and sizes.`), { user: true });
          this.busy = `Making ${pd.design.pieces.length} name and number files…`; this.render();
          for (let i = 0; i < pd.design.pieces.length; i += 4) {
            const batch = pd.design.pieces.slice(i, i + 4).map(async (x, j) => this.store(await R.printBlob(await R.artwork(p, color, { location: pd.location, design: { ...pd.design, show: i + j } })), `art-${pd.location}-${i + j + 1}.png`));
            pieceFiles = pieceFiles.concat(await Promise.all(batch));
          }
          this.busy = 'Saving your design…'; this.render();
        }
        const t = q.type;
        // Files: mockup (JPEG), each location's print file (PNG, 150 dpi), each original upload.
        const mockCanvas = await R.mockup(p, color, decs);
        if (mockCanvas.missing) throw Object.assign(new Error('Your design did not load. Go back and add the file again, as a PNG or JPG.'), { user: true });
        const mockBlob = await R.toBlob(mockCanvas, 'image/jpeg', 0.85);
        const [mockUrl, ...locFiles] = await Promise.all([
          this.store(mockBlob, 'mockup.jpg'),
          ...decs.map(async (d) => ({
            art: await this.store(await R.printBlob(await R.artwork(p, color, d)), `art-${d.location}.png`),
            info: await R.printInfo(p, color, d).catch(() => null),
            // The shopper's original file (PNG, JPG, PDF…). It is for staff; the print file is _art. A failed upload of it
            // must not stop the order, so it is kept null and the order still goes in.
            upload: d.design.kind === 'upload' && d.design.file ? await this.store(d.design.file, d.design.name || 'upload').catch(() => null) : null,
          })),
        ]);
        const summary = decs.map((d) => this.describe(p, color, d, g).label).join(', ');
        const contact = e.contact.value;
        const common = sample
          ? { 'Sample order': id, 'Order type': `Sample — printed (${t.title})`, Decoration: `${t.title}: ${summary}`, _order: id, _sample: 'printed', _min: '1', _pieces: '1' }
          : { 'Decorated order': id, Decoration: `${t.title}: ${summary}`, _order: id, _min: String(q.minimum), _pieces: String(q.pieces) };
        common.Design = 'Customer designed (Studio v2)';
        if (this.pickedTitle) common.Idea = this.pickedTitle;
        if (this.state.brief.text) common.Brief = this.state.brief.text.slice(0, 300);
        if (this.state.brief.image) {
          const pol = C.imagePolicy(this.state.brief.image.cls);
          common['Upload type'] = `${pol.label}${pol.subject ? ` (${pol.subject.slice(0, 80)})` : ''}: ${pol.asIs ? 'printed as it is or restyled' : 'artwork drawn from it'} · ${pol.source ? `${pol.source === 'shopper' ? 'shopper said' : 'AI classifier'}` : 'not classified'}`;
        }
        if (contact) common.Contact = [contact.email && `Email: ${contact.email}`, contact.sms && `SMS: ${contact.sms}`].filter(Boolean).join(' · ');
        // "Send me a proof first" (review screen, Phase 2) asks for the same thing as a saved contact did.
        const proof = !!this.memo.proof;
        common['Art approval'] = proof ? 'Yes — send me a proof first' : contact ? 'Yes — contact me before production' : 'No';
        common._studio = 'v2';
        const deco = q.fees.map((x, i) => {
          const d = decs[i], info = this.describe(p, color, d, g);
          const props = { ...common, Location: `${info.label} (${d.location})`, Method: `${t.title} — ${x.sv.label} (${x.sv.code})`, 'Print size': info.size, Artwork: info.art, Garment: `${p.title} — ${color}`, _role: 'decoration', _location: d.location, _service: x.sv.code, _art: locFiles[i].art, _mockup: mockUrl };
          if (locFiles[i].upload) props._upload_1 = locFiles[i].upload;
          // A per-piece location: _art is the file of the piece shown in the Studio (for a glance); every garment line carries its own (_piece_art).
          if (d === pd) props._art_per_piece = `yes: ${pieceFiles.length} files, one per garment line (_piece_art)`;
          const pi = locFiles[i].info;
          if (pi && pi.inches) props['Design size'] = `${pi.inches[0].toFixed(1)} × ${pi.inches[1].toFixed(1)} in`;
          const check = artNotes(pi);
          if (check.length) props['Art check'] = check.join(' ');
          if (t.ink) props['Ink colors'] = String(t.services.indexOf(x.sv) + 1);
          return { id: x.fee.id, quantity: q.pieces, properties: props };
        });
        // Per-piece: one garment line per piece (quantity 1) with its size, name, number and print file, which the order desk
        // passes to the POD placement as one line item each (tasks/shopmvsport-revamp-phase3/scripts/piece-lines.py).
        const blanks = pd
          ? pd.design.pieces.map((x, i) => ({ id: p.variants.find((v) => v.size === x.size && (v.color == null || v.color === color)).id, quantity: 1, properties: { ...common, _role: 'blank', _mockup: mockUrl, 'Name and number': [x.name, x.number].filter(Boolean).join(' '), _piece: `${i + 1} of ${pd.design.pieces.length}`, _piece_size: x.size, _piece_name: x.name, _piece_number: x.number, _piece_location: pd.location, _piece_art: pieceFiles[i] } }))
          : q.lines.slice().reverse().map((l) => ({ id: l.v.id, quantity: l.n, properties: { ...common, _role: 'blank', _mockup: mockUrl } }));
        if (q.setup && this.cat.setupFee) blanks.unshift({ id: this.cat.setupFee.id, quantity: 1, properties: { ...common, _role: 'setup', _mockup: mockUrl } });
        // Checkout in plain words (Cal, 2026-10-12: "remove the production jargon from checkout"). Every production
        // field moves under the same name with a leading "_" ("Method" -> "_Method"): Shopify hides those keys in the
        // cart, checkout and customer emails but shows them on the admin order page, where staff read them. Nothing
        // automated reads the visible names (checked 2026-10-12: MVDev, the checkout function and monitors read only
        // "_" keys). What the shopper sees on each line is just the design and how it is printed.
        const how = (x) => (t.ink ? `${t.services.indexOf(x.sv) + 1} color screen print` : String(x.sv.label || 'full color').toLowerCase());
        const designName = sample ? 'Printed sample (one piece)' : (this.pickedTitle || 'Your design');
        const spots = decs.map((d) => this.describe(p, color, d, g).label).join(', ');
        const plain = (props, visible) => Object.assign({}, visible, Object.fromEntries(Object.entries(props).map(([k, v]) => [k[0] === '_' ? k : `_${k}`, v])));
        deco.forEach((it, i) => { it.properties = plain(it.properties, { Design: designName, Print: `${this.describe(p, color, decs[i], g).label} · ${how(q.fees[i])}` }); });
        blanks.forEach((it) => { const nn = it.properties['Name and number']; it.properties = plain(it.properties, { Design: designName, Print: q.fees.length ? `${spots} · ${how(q.fees[0])}` : spots, ...(nn ? { 'Name and number': nn } : {}) }); });
        this.busy = 'Adding to your cart…'; this.render();
        await this.post(this.d.cartAddUrl, { items: [...deco, ...blanks] });
        // H5 "carts as bookmarks": a cart that was empty before this add starts now. The hidden cart attributes
        // _cart_started / _cart_session let snippets/posthog.liquid tell a returning shopper (cart_present,
        // returned: true) from the same visit. Attributes starting with "_" are hidden at checkout.
        if (!before || !before.item_count) {
          try {
            await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { _cart_started: new Date().toISOString(), _cart_session: sid() || '', _cart_src: this.ctx.entry.ad || this.ctx.entry.source || '' } }) });
          } catch (_) {}
          track('cart_created', this.props({ order: id, total: q.total / 100, pieces: q.pieces, dest, cart_token: (before && before.token) || null }));
        }
        // The proof choice on the order itself (cart attribute → order attribute "Proof"), read by the order desk. One proof
        // tick covers the whole order; an order without one keeps whatever an earlier Studio order in the cart said.
        if (proof) {
          try { await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { Proof: 'Send me a proof first', _proof: '1' } }) }); } catch (_) {}
        }
        if (contact && window.mvIdentify) window.mvIdentify(contact.email || contact.sms, contact.email ? { email: contact.email } : { phone: contact.sms });
        if (window.StudioKeep) window.StudioKeep.clear(); // in the cart now: the next Studio visit starts clean
        track('studio2_added_to_cart', this.props({ dest, order: id, proof, ms: Date.now() - t0, pieces: q.pieces, total: q.total / 100, sample, locations: decs.map((d) => d.location), kinds: decs.map((d) => d.design.kind), art_style: C.artStyle(decs[0] && decs[0].design), upload_class: this.uploadClass || null }));
        if (dest === 'checkout') {
          this.busy = 'Taking you to checkout…';
          this.render();
          if (window.mvCheckoutStarted) window.mvCheckoutStarted('studio', { order: id, total: q.total / 100, pieces: q.pieces, dest });
          location.href = '/checkout';
        } else {
          // "Add to cart" no longer opens the cart page (H2, Cal 2026-10-12): the shopper stays here and gets
          // "Check out" (straight to Shopify checkout) or "Keep shopping".
          const after = await this.loadCart();
          this.busy = null;
          this.added = { order: id, cartCents: after ? after.total_price : this.cartCents, cartGrams: after ? after.total_weight : this.cartGrams, items: after ? after.items.filter((i) => i.properties && i.properties._role === 'blank').reduce((n, i) => n + i.quantity, 0) : q.pieces };
          track('studio2_added_panel', this.props({ order: id, cart_total: this.added.cartCents / 100 }));
          this.render();
        }
      } catch (err) {
        this.busy = null;
        track('studio2_add_to_cart_failed', this.props({ dest, error: String(err.message || err).slice(0, 300), ms: Date.now() - t0 }));
        this.toast(err.user ? err.message : `Nothing was added to the cart (${err.message}). Please try again.`);
        this.render();
      }
    }
  }
  if (!customElements.get('studio-v2')) customElements.define('studio-v2', StudioV2);
})();
